import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Seo from '../components/Seo';
import { ChevronRight, ChevronLeft, Shield, Heart, Phone, Mail, X, Star, Truck, Camera, Smartphone, Plus, Sparkles, BookOpen, MapPin, FileText, Users, Droplets, Moon, Activity, ThermometerSun, Award, Gift } from 'lucide-react';
import { ASSETS, CONTACT, TRY_BEFORE_YOU_BUY_IMAGE, TRY_BEFORE_YOU_BUY_URL } from '../data/constants';
import { HOT_TUBS, SWIM_SPAS, COLD_PLUNGES, SAUNAS } from '../data/products';
import { PDC_SPAS_PRODUCTS } from '../data/pdcSpas';
import { LEISURECRAFT_OUTDOOR_SHOWERS, LEISURECRAFT_OUTDOOR_FURNITURE } from '../data/outdoorCatalogs';
import { VIDEOS } from '../data/mediaAssets';
import { VideoBlock } from '../components/VideoBlock';
import { REELS } from '../data/reels';
import { WhyBuySection } from '../components/WhyBuySection';
import { FaqSection, faqJsonLd } from '../components/FaqSection';
import { PAGE_COPY, BUSINESS } from '../data/seoContent';

// Normalize PDC products (different shape) into the shared product card shape
const PDC_CATEGORY_LABEL = { 'hot-tubs': 'Hot Tub', 'swim-spas': 'Swim Spa', 'cold-plunges': 'Cold Plunge' };
const normalizePDC = (p) => {
  const seatsNum = typeof p.seats === 'string' ? parseInt(p.seats, 10) : p.seats;
  return {
    ...p,
    brand: 'PDC Spas',
    persons: Number.isNaN(seatsNum) ? null : seatsNum,
    seatsLabel: typeof p.seats === 'string' ? p.seats : null,
    price: 'Call for Pricing',
    priceValue: 9999999, // sort to the end (no MSRP)
    category: PDC_CATEGORY_LABEL[p.category] || 'Hot Tub',
    isPDC: true,
    images: { primary: Array.isArray(p.images) ? p.images[0] : p.images?.primary },
  };
};
const PDC_HOT_TUBS = PDC_SPAS_PRODUCTS.filter(p => p.category === 'hot-tubs').map(normalizePDC);
const PDC_SWIM_SPAS = PDC_SPAS_PRODUCTS.filter(p => p.category === 'swim-spas').map(normalizePDC);
const PDC_COLD_PLUNGES = PDC_SPAS_PRODUCTS.filter(p => p.category === 'cold-plunges').map(normalizePDC);

// Build the correct detail-page link for any product (PDC lives on its own route)
const productLink = (product) => product.isPDC ? `/pdc-spas/${product.id}` : `/products/${product.id}`;

// Reusable gradient background style - Clean Tropical Theme (no shapes)
const gradientBg = {
  background: 'linear-gradient(180deg, #ffffff 0%, #E0FFFF 15%, #B0E0E6 30%, #98FB98 50%, #F0FFF0 70%, #FFFACD 85%, #FFE4B5 100%)'
};

const lightGradientBg = {
  background: 'linear-gradient(180deg, #ffffff 0%, rgba(224, 255, 255, 0.6) 25%, rgba(152, 251, 152, 0.4) 50%, rgba(255, 250, 205, 0.5) 75%, #ffffff 100%)'
};

// Sort products by price (least to most expensive)
const sortByPrice = (products) => {
  return [...products].sort((a, b) => (a.priceValue || 0) - (b.priceValue || 0));
};

const ALL_PRODUCTS = [
  ...sortByPrice(HOT_TUBS),
  ...sortByPrice(SWIM_SPAS),
  ...sortByPrice(SAUNAS),
  ...sortByPrice(COLD_PLUNGES),
];

// Try Before You Buy popup — click the graphic to book a showroom visit
const WetTestPopup = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70"
        onClick={onClose}
        data-testid="try-before-you-buy-popup"
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0, y: 50 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="relative w-full max-w-[400px] max-h-[92vh] flex flex-col"
          onClick={e => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute -top-3 -right-3 text-white p-2 z-10 bg-black/70 hover:bg-black rounded-full shadow-lg transition-colors"
            aria-label="Close popup"
            data-testid="popup-close"
          >
            <X size={22} />
          </button>

          <Link
            to={TRY_BEFORE_YOU_BUY_URL}
            onClick={onClose}
            className="block rounded-xl overflow-hidden shadow-2xl ring-4 ring-white/20 hover:ring-[#F88200]/60 transition-all cursor-pointer min-h-0"
            data-testid="popup-book-link"
          >
            <img
              src={TRY_BEFORE_YOU_BUY_IMAGE}
              alt="Try before you buy at our Naples showroom — hot tub wet test, sauna trial and cold plunge challenge. Towels, robes and slippers provided, complimentary beer and wine (21+). Call 239-517-3135. 6000 Taylor Rd, Suite 8, Naples, FL."
              className="w-full h-auto max-h-[78vh] object-contain block bg-[#0E3D95]"
            />
          </Link>

          <div className="flex gap-2 mt-3">
            <a href={`tel:${BUSINESS.tel}`} className="flex-1 text-center bg-[#F88200] hover:bg-[#e07600] text-white font-bold py-2.5 rounded-full transition-colors" data-testid="popup-call-btn">
              Call {BUSINESS.phone}
            </a>
            <Link to={TRY_BEFORE_YOU_BUY_URL} onClick={onClose} className="flex-1 text-center bg-white hover:bg-slate-100 text-[#0E3D95] font-bold py-2.5 rounded-full transition-colors" data-testid="popup-book-btn">
              Book a Visit
            </Link>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// NEW Hero Section - Video without text overlay, text below - Clean Tropical Gradient background
const HeroSection = () => (
  <section className="relative pt-24 md:pt-32 lg:pt-40 overflow-hidden" style={{
    background: 'linear-gradient(180deg, #ffffff 0%, #E0FFFF 15%, #AFEEEE 30%, #98FB98 50%, #F0FFF0 70%, #FFFACD 85%, #FFE4B5 100%)'
  }} data-testid="hero-section">
    {/* Store Opening Soon Banner */}
    <div className="max-w-5xl mx-auto px-4 mb-6">
      <motion.div 
        initial={{ opacity: 0, y: -20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ duration: 0.6 }}
        className="bg-gradient-to-r from-[#F88200] via-[#FF9A1F] to-[#F88200] text-white rounded-xl p-4 md:p-5 text-center shadow-lg border-2 border-white/30"
      >
        <div className="flex items-center justify-center gap-2 mb-1">
          <Sparkles className="w-5 h-5 md:w-6 md:h-6" />
          <span className="font-['Barlow_Condensed'] text-xl md:text-2xl lg:text-3xl font-black uppercase tracking-wide">
            Store Opening Soon!
          </span>
          <Sparkles className="w-5 h-5 md:w-6 md:h-6" />
        </div>
        <p className="text-white/90 text-sm md:text-base font-medium">
          Keep checking back. Order today and get it delivered!
        </p>
      </motion.div>
    </div>

    {/* Video Section - Smaller to fit fully */}
    <div className="max-w-4xl mx-auto px-4 relative z-10">
      <div className="relative aspect-video rounded-lg overflow-hidden shadow-2xl">
        <video autoPlay muted loop playsInline className="w-full h-full object-contain bg-black">
          <source src={ASSETS.heroVideo} type="video/mp4" />
        </video>
      </div>
    </div>
    
    {/* Text Content Below Video - Red White Blue Theme */}
    <div className="py-6 md:py-10">
      <div className="max-w-6xl mx-auto px-4 text-center">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          {/* Animated American Flags - More realistic waving effect */}
          <div className="flex items-center justify-center gap-4 mb-4">
            <motion.div
              animate={{ 
                rotateY: [0, 10, 0, -10, 0],
                skewX: [0, 2, 0, -2, 0]
              }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="relative"
            >
              <img 
                src="https://flagcdn.com/w160/us.png"
                alt="American Flag"
                className="w-14 h-9 md:w-20 md:h-12 object-cover rounded shadow-lg border border-slate-600"
              />
            </motion.div>
            <span className="text-lg md:text-2xl font-bold tracking-widest uppercase text-[#0E3D95]">American Made & Proud of It</span>
            <motion.div
              animate={{ 
                rotateY: [0, -10, 0, 10, 0],
                skewX: [0, -2, 0, 2, 0]
              }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="relative"
            >
              <img 
                src="https://flagcdn.com/w160/us.png"
                alt="American Flag"
                className="w-14 h-9 md:w-20 md:h-12 object-cover rounded shadow-lg border border-slate-600"
              />
            </motion.div>
          </div>
          
          <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight mb-3 text-[#0E3D95]" data-testid="home-h1">
            Hot Tubs, Swim Spas, Saunas & Cold Plunges in Naples, FL
          </h1>
          <p className="font-['Barlow_Condensed'] text-2xl sm:text-3xl md:text-5xl font-black uppercase tracking-tight mb-4">
            <span className="whitespace-nowrap">Live Your{' '}
            <span 
              className="text-[#F88200]"
              style={{ 
                textShadow: '-2px -2px 0 #fff, 2px -2px 0 #fff, -2px 2px 0 #fff, 2px 2px 0 #fff, -3px 0 0 #fff, 3px 0 0 #fff, 0 -3px 0 #fff, 0 3px 0 #fff'
              }}
            >
              Healthiest Life
            </span></span>
            <br />
            <span className="text-[#0E3D95]">While Enjoying a Vacation</span>
            <br />
            <span className="text-[#0E3D95]">Everyday at Home</span>
          </p>

          <p className="text-lg md:text-2xl font-bold mb-4 text-[#0E3D95]" data-testid="home-subhead">
            Live your healthiest life. Call our wellness experts today:{' '}
            <a href={`tel:${BUSINESS.tel}`} className="text-[#F88200] hover:underline whitespace-nowrap">{BUSINESS.phone}</a>
          </p>

          <p className="text-base md:text-lg mb-6 max-w-4xl mx-auto text-slate-700 leading-relaxed" data-testid="home-intro">
            {PAGE_COPY.home}
          </p>

          <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-center mb-4">
            <a href={`tel:${BUSINESS.tel}`} className="btn-primary inline-flex items-center justify-center gap-2 text-lg px-8 py-4" data-testid="home-call-btn">
              Call {BUSINESS.phone}
            </a>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 text-lg px-8 py-4 rounded-full bg-[#0E3D95] text-white font-bold hover:bg-[#0b3077] transition-colors" data-testid="home-book-visit-btn">
              Book a showroom visit
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 text-lg px-8 py-4 rounded-full border-2 border-[#0E3D95] text-[#0E3D95] font-bold hover:bg-[#0E3D95] hover:text-white transition-colors" data-testid="home-quote-btn">
              Get a quote <ChevronRight size={22} />
            </Link>
          </div>
          
          <p className="text-lg md:text-xl font-bold text-[#F88200]">
            Hot Tubs • Swim Spas • Saunas • Cold Plunges — Serving Naples & all of Southwest Florida
          </p>
        </motion.div>
      </div>
    </div>
  </section>
);

// Trust Badges Section - White background, compact
const TrustBadgesSection = () => (
  <section className="py-3 border-b border-slate-200" style={lightGradientBg}>
    <div className="max-w-7xl mx-auto px-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
        <div className="flex flex-col items-center gap-0.5 p-2">
          <Shield className="w-5 h-5 text-[#F88200]" />
          <span className="font-['Barlow_Condensed'] text-xs md:text-sm font-bold uppercase text-[#0E3D95]">5% Discount</span>
          <span className="text-[10px] text-slate-500">Military & Veterans</span>
        </div>
        <div className="flex flex-col items-center gap-0.5 p-2">
          <span className="text-xl">🇺🇸</span>
          <span className="font-['Barlow_Condensed'] text-xs md:text-sm font-bold uppercase text-[#0E3D95]">American Made</span>
          <span className="text-[10px] text-slate-500">Made in USA</span>
        </div>
        <div className="flex flex-col items-center gap-0.5 p-2">
          <Heart className="w-5 h-5 text-[#F88200]" />
          <span className="font-['Barlow_Condensed'] text-xs md:text-sm font-bold uppercase text-[#0E3D95]">Family Owned</span>
          <span className="text-[10px] text-slate-500">American & Proud</span>
        </div>
        <div className="flex flex-col items-center gap-0.5 p-2">
          <Truck className="w-5 h-5 text-[#0E3D95]" />
          <span className="font-['Barlow_Condensed'] text-xs md:text-sm font-bold uppercase text-[#0E3D95]">Delivery Available</span>
          <span className="text-[10px] text-slate-500">Southwest Florida</span>
        </div>
      </div>
    </div>
  </section>
);

// NEW Best Warranty Section with gradient and links - All warranties listed equally
const BestWarrantySection = () => (
  <section className="py-10 md:py-14 relative overflow-hidden" style={{
    background: 'linear-gradient(135deg, #006994 0%, #20B2AA 50%, #2E8B57 100%)'
  }}>
    <div className="max-w-6xl mx-auto px-4 relative z-10">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center"
      >
        <div className="flex items-center justify-center gap-4 mb-4">
          <motion.div
            animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            <Award className="w-12 h-12 md:w-16 md:h-16 text-[#FFD700]" />
          </motion.div>
          <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl lg:text-6xl font-black uppercase text-white">
            Best <span className="text-[#FFD700]">Warranty</span> in the Business
          </h2>
          <motion.div
            animate={{ rotate: [0, -10, 10, 0], scale: [1, 1.1, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            <Award className="w-12 h-12 md:w-16 md:h-16 text-[#FFD700]" />
          </motion.div>
        </div>
        <p className="text-lg md:text-xl lg:text-2xl text-white/90 max-w-4xl mx-auto mb-6">
          We stand behind every hot tub, swim spa, sauna and cold plunge we sell. Our industry-leading warranty gives you peace of mind and total confidence.
        </p>
        <p className="text-base md:text-lg text-[#FFD700] font-semibold mb-8">
          All warranties include parts and labor! We have our own in-house tech!
        </p>
        
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          <Link to="/pdc-spas" className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20 hover:bg-white/20 transition-colors">
            <span className="text-white font-bold text-lg">PDC Spas Warranty →</span>
          </Link>
        </div>
        <div className="flex flex-wrap justify-center gap-6">
          <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20">
            <span className="text-[#FFD700] font-bold text-lg">✓ Comprehensive Coverage</span>
          </div>
          <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20">
            <span className="text-[#FFD700] font-bold text-lg">✓ Local Service</span>
          </div>
          <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20">
            <span className="text-[#FFD700] font-bold text-lg">✓ Fast Response</span>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

// Included Items Section - What comes with the hot tub price
const FreeItemsSection = () => {
  const includedItems = [
    { name: 'Spa Cover', image: ASSETS.freeItems.cover },
    { name: 'Steps', image: ASSETS.freeItems.steps },
    { name: 'Start-Up Chemicals', image: ASSETS.freeItems.chemicals },
  ];

  return (
    <section className="py-12 md:py-16 overflow-hidden" style={gradientBg}>
      <div className="max-w-7xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Gift className="w-10 h-10 text-[#F88200]" />
            <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl lg:text-5xl font-black uppercase text-[#0E3D95]">
              <span className="text-[#F88200]">Included</span> With Your Hot Tub Purchase
            </h2>
            <Gift className="w-10 h-10 text-[#F88200]" />
          </div>
          <p className="text-xl md:text-2xl font-bold text-[#D4AF37] mb-2">
            A $1,500 Value — Included in the Price!
          </p>
          <p className="text-lg text-slate-600">
            Cover • Steps • Start-Up Chemicals
          </p>
        </motion.div>

        {/* Included Items Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8 max-w-4xl mx-auto">
          {includedItems.map((item, idx) => (
            <motion.div
              key={item.name}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.15 }}
              className="flex flex-col items-center"
            >
              <div className="relative mb-4">
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#F88200]/20 to-[#D4AF37]/20 rounded-full blur-xl scale-110" />
                
                {/* Product Image */}
                <div className="relative bg-white rounded-2xl shadow-xl p-4 border border-slate-100">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-32 h-32 md:w-40 md:h-40 object-contain"
                  />
                </div>
                
                {/* INCLUDED Badge */}
                <div className="absolute -top-3 -right-3 bg-[#0E3D95] text-white font-bold text-xs px-3 py-1 rounded-full shadow-lg uppercase tracking-wide">
                  Included
                </div>
              </div>
              
              {/* Item Name */}
              <h3 className="font-['Barlow_Condensed'] text-lg md:text-xl font-bold uppercase text-[#0E3D95] text-center">
                {item.name}
              </h3>
            </motion.div>
          ))}
        </div>

        {/* Optional Add-On: Cover Lifter & Railing */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <Link to="/covers" className="inline-flex flex-col sm:flex-row items-center gap-3 bg-white border-2 border-[#F88200] rounded-xl px-6 py-4 shadow-lg max-w-2xl hover:bg-orange-50 transition-colors">
            <span className="font-['Barlow_Condensed'] text-xl md:text-2xl font-bold uppercase text-[#0E3D95]">
              Optional Add-Ons
            </span>
            <span className="text-base md:text-lg text-slate-700">
              Cover Lifters, Hand Rails, Custom Covers &amp; Steps — <span className="font-bold text-[#F88200]">see Covers &amp; Lifters</span>
            </span>
          </Link>
          <p className="text-sm text-slate-500 mt-4 max-w-2xl mx-auto">
            Shipping is not included and is calculated by your delivery address. Delivery, installation &amp; set-up quoted separately.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// NEW Try Before You Buy - Wet Test Section - Filled with logo, images, big text
const WetTestSection = () => (
  <section style={lightGradientBg} data-testid="wet-test-section">
    <div className="flex flex-col md:flex-row items-stretch">
      {/* Video Side - Full bleed */}
      <motion.div
        initial={{ opacity: 0, x: -30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        className="md:w-1/2"
      >
        <video autoPlay muted loop playsInline className="w-full h-full object-cover">
          <source src={ASSETS.wetTestVideo} type="video/mp4" />
        </video>
      </motion.div>
      
      {/* Content Side - Packed with logo, big text, images */}
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        className="md:w-1/2 text-[#0E3D95] p-6 md:p-8 lg:p-10 flex flex-col justify-between"
        style={{ background: 'linear-gradient(135deg, #ffffff 0%, #E0FFFF 30%, #AFEEEE 60%, #98FB98 100%)' }}
      >
        {/* Logo at top - Use OLD logo for Family Owned section - BIGGER */}
        <div className="flex flex-col items-center mb-6">
          <img src={ASSETS.tropicalLogo} alt="Leisure Wellness Naples" className="h-40 md:h-48 lg:h-56 object-contain mb-4" />
          <div className="text-center">
            <p className="font-['Barlow_Condensed'] text-2xl md:text-3xl lg:text-4xl font-bold uppercase tracking-wider text-[#0E3D95]">Family Owned</p>
            <p className="font-['Barlow_Condensed'] text-xl md:text-2xl text-[#F88200] font-semibold">American Made & Proud</p>
          </div>
        </div>

        {/* Main heading */}
        <div className="border-l-4 border-[#F88200] pl-5 mb-4">
          <h3 className="font-['Barlow_Condensed'] text-4xl md:text-5xl lg:text-6xl font-black uppercase leading-none mb-1 text-[#0E3D95]">
            Try Before You Buy
          </h3>
          <h3 className="font-['Barlow_Condensed'] text-4xl md:text-5xl lg:text-6xl font-black uppercase leading-none text-[#F88200]">
            "Wet Test"
          </h3>
        </div>
        
        <p className="text-xl md:text-2xl lg:text-3xl mb-4 text-slate-700">
          Bring your suits — we have <span className="font-bold text-[#0E3D95]">robes, slippers, and towels</span>. Try today!
        </p>
        
        <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl lg:text-5xl font-black uppercase mb-3 text-[#0E3D95]">
          Why <span className="text-[#F88200]">Leisure Wellness Naples</span>?
        </h2>
        
        <ul className="space-y-2 mb-5 text-lg md:text-xl lg:text-2xl">
          <li className="flex items-start gap-3 text-slate-700">
            <div className="w-3 h-3 bg-[#F88200] rounded-full flex-shrink-0 mt-2" />
            Serving Naples &amp; all of Southwest Florida
          </li>
          <li className="flex items-start gap-3 text-slate-700">
            <div className="w-3 h-3 bg-[#F88200] rounded-full flex-shrink-0 mt-2" />
            Hot tub price includes Cover, Steps &amp; Start-Up Chemicals <span className="font-bold text-[#F88200]">— a $1,500 value</span>
          </li>
          <li className="flex items-start gap-3 text-slate-700">
            <div className="w-3 h-3 bg-[#F88200] rounded-full flex-shrink-0 mt-2" />
            Military &amp; First Responder discounts
          </li>
        </ul>
        
        <div className="flex flex-wrap gap-3">
          <Link to="/contact" className="bg-[#F88200] hover:bg-[#e07600] text-white inline-flex items-center gap-2 text-lg md:text-xl font-bold py-3 px-8 uppercase tracking-wider transition-colors">
            Schedule Wet Test <ChevronRight size={22} />
          </Link>
          <Link to="/about" aria-label="Learn more about Leisure Wellness Naples" className="border-2 border-[#0E3D95] hover:bg-[#0E3D95] hover:text-white text-[#0E3D95] inline-flex items-center gap-2 text-lg md:text-xl font-bold py-3 px-8 uppercase tracking-wider transition-colors">
            Learn More About Us <ChevronRight size={22} />
          </Link>
        </div>
      </motion.div>
    </div>
  </section>
);

// NEW Prominent Wet Test Banner
const WetTestBanner = () => (
  <section className="py-16 md:py-20 bg-gradient-to-r from-[#F88200] to-[#991B1B] text-white">
    <div className="max-w-6xl mx-auto px-4 text-center">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <h2 className="font-['Barlow_Condensed'] text-4xl md:text-6xl lg:text-7xl font-black uppercase mb-4">
          Try Before You Buy
        </h2>
        <h3 className="font-['Barlow_Condensed'] text-3xl md:text-5xl font-bold uppercase mb-6 text-white/90">
          "Wet Test"
        </h3>
        <p className="text-xl md:text-2xl lg:text-3xl mb-8 max-w-4xl mx-auto">
          Bring your suits — we have <span className="font-bold">robes, slippers, and towels</span>. Try today!
        </p>
        <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-[#F88200] px-10 py-5 font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider hover:bg-slate-100 transition-all">
          Schedule Your Wet Test <ChevronRight size={24} />
        </Link>
      </motion.div>
    </div>
  </section>
);

// NEW Resource Cards Section (like File 4 - 3 large cards) - Original Images
const ResourceCardsSection = () => {
  const cards = [
    {
      title: 'Owner Resources',
      image: '/images/gallery/Lady-Relaxing_f84cdca3f3fd7df924a9ef2738140c50.jpg',
      link: '/about',
    },
    {
      title: 'Get a Brochure',
      image: '/images/gallery/Caldera-Paradise-Kauai-Hot-Tub-Sterling-Marble-Coastal-Gray-Lifestyle-Couple-007-scaled.jpg',
      link: '/brochures',
    },
    {
      title: 'Visit Our Showroom',
      image: 'https://customer-assets.emergentagent.com/job_901d85eb-9d74-4232-877c-8dd79f35bfb5/artifacts/3a41thwj_1.png',
      link: '/contact',
    },
  ];

  return (
    <section className="py-12" style={lightGradientBg}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid md:grid-cols-3 gap-6">
          {cards.map((card, idx) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
            >
              <Link 
                to={card.link}
                className="group block relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all h-64 md:h-80"
              >
                <img 
                  src={card.image} 
                  alt={card.title}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold text-white mb-2">
                    {card.title}
                  </h3>
                  <span className="inline-flex items-center gap-2 text-white font-semibold text-lg group-hover:gap-3 transition-all">
                    LEARN MORE <ChevronRight size={20} />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// NEW Product Collection Grid (like File 5) - Gradient background
const ProductCollectionSection = () => {
  const collections = [
    {
      name: 'Hot Tubs',
      subtitle: 'PDC Spas',
      tagline: 'Premium American Craftsmanship',
      link: '/hot-tubs',
      image: '/images/gallery/Caldera-Paradise-Kauai-Hot-Tub-Sterling-Marble-Coastal-Gray-Lifestyle-Couple-007-scaled.jpg',
    },
    {
      name: 'Saunas',
      subtitle: 'Indoor & Outdoor Saunas',
      tagline: 'Relaxation & Detoxification',
      link: '/saunas',
      image: SAUNAS[0]?.images?.primary || 'https://images.unsplash.com/photo-1574427386945-ae011838ee9a?w=600',
    },
    {
      name: 'Cold Plunges',
      subtitle: 'Cold Therapy Benefits',
      tagline: 'Recovery & Wellness',
      link: '/cold-plunges',
      image: COLD_PLUNGES[0]?.images?.primary || 'https://images.unsplash.com/photo-1574427386945-ae011838ee9a?w=600',
    },
    {
      name: 'Swim Spas',
      subtitle: 'Exercise & Relaxation',
      tagline: 'Swim, Exercise and Relax',
      link: '/swim-spas',
      image: 'https://b4087952.smushcdn.com/4087952/wp-content/uploads/2024/06/Valhalla-Overhead-square-1.jpg?lossy=2&strip=1&webp=1',
    },
    {
      name: 'Outdoor Showers',
      subtitle: 'Cedar & Barrel Showers',
      tagline: 'Rinse Off in Style',
      link: '/leisurecraft#showers',
      image: LEISURECRAFT_OUTDOOR_SHOWERS[0]?.image || 'https://images.unsplash.com/photo-1574427386945-ae011838ee9a?w=600',
    },
    {
      name: 'Outdoor Furniture',
      subtitle: 'Tiki Bars, Chairs & Tables',
      tagline: 'Handcrafted Cedar Living',
      link: '/leisurecraft#furniture',
      image: LEISURECRAFT_OUTDOOR_FURNITURE[0]?.image || 'https://images.unsplash.com/photo-1574427386945-ae011838ee9a?w=600',
    },
  ];

  return (
    <section className="py-16" style={{
      background: 'linear-gradient(180deg, #ffffff 0%, #E0FFFF 20%, #AFEEEE 50%, #98FB98 80%, #FFFACD 100%)',
      backgroundImage: `
        linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(220,240,250,0.8) 30%, rgba(180,220,245,0.7) 70%, rgba(160,210,240,0.8) 100%),
        url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 320'%3E%3Cpath fill='%23ffffff' fill-opacity='0.4' d='M0,96L48,112C96,128,192,160,288,186.7C384,213,480,235,576,218.7C672,203,768,149,864,128C960,107,1056,117,1152,138.7C1248,160,1344,192,1392,208L1440,224L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z'%3E%3C/path%3E%3C/svg%3E")`
    }}>
      <div className="max-w-7xl mx-auto px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl lg:text-6xl font-black uppercase text-[#0E3D95] mb-4">
            Expand Your{' '}
            <span 
              className="text-[#F88200]"
              style={{ 
                textShadow: '-2px -2px 0 #fff, 2px -2px 0 #fff, -2px 2px 0 #fff, 2px 2px 0 #fff, -3px 0 0 #fff, 3px 0 0 #fff, 0 -3px 0 #fff, 0 3px 0 #fff'
              }}
            >
              Wellness Journey
            </span>
          </h2>
          <p className="text-lg md:text-xl text-slate-600">Find the perfect product for your lifestyle</p>
        </motion.div>

        {/* Cards Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {collections.map((item, idx) => (
            <motion.div
              key={item.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
            >
              <Link 
                to={item.link}
                className="group block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all h-full"
              >
                <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                  <img 
                    src={item.image} 
                    alt={item.name}
                    className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => e.target.src = ASSETS.logo}
                  />
                </div>
                <div className="p-6 text-center">
                  <h3 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold uppercase text-[#0E3D95] group-hover:text-[#F88200] transition-colors">
                    {item.name}
                  </h3>
                  <p className="text-base md:text-lg text-[#F88200] font-semibold mt-1">{item.subtitle}</p>
                  <p className="text-sm md:text-base text-slate-500 mt-2">{item.tagline}</p>
                  <span className="inline-flex items-center gap-2 text-[#F88200] text-lg font-bold mt-4 group-hover:gap-3 transition-all">
                    Learn More <ChevronRight size={20} />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// NEW The Collection - Brand Based Section + Sauna Collection
const LocationCollectionSection = () => {
  const brands = [
    { name: 'PDC Spas', link: '/pdc-spas', border: 'border-[#F88200]', tagline: 'American Made Premium Quality' },
    { name: 'Clearwater Spas', link: '/clearwater-spas', border: 'border-[#0E3D95]', tagline: 'Affordable Handcrafted Luxury' },
    { name: 'Natural Rock Spas', link: '/natural-rock-spas', border: 'border-[#20B2AA]', tagline: 'Natural Stone-Look Spas' },
  ];

  const saunas = [
    {
      name: 'Serenity 2',
      tagline: 'Smart Infrared Sauna · 1–2 Person',
      link: '/products/sauna-health-mate-serenity-2',
      image: '/images/saunas/serenity-2/serenity-2-main.webp',
    },
    {
      name: 'Serenity 3',
      tagline: 'Smart Infrared Sauna · 1–3 Person',
      link: '/products/sauna-health-mate-serenity-3',
      image: '/images/saunas/serenity-3/serenity-3-5.jpg',
    },
  ];

  return (
    <section className="py-16 md:py-20" style={gradientBg}>
      <div className="max-w-7xl mx-auto px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          {/* Subtle accent line */}
          <div className="flex justify-center mb-6">
            <div className="flex items-center gap-2">
              <div className="w-16 h-0.5 bg-[#0E3D95]"></div>
              <div className="w-6 h-0.5 bg-[#F88200]"></div>
              <div className="w-16 h-0.5 bg-[#0E3D95]"></div>
            </div>
          </div>
          <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl lg:text-6xl font-black uppercase mb-4 text-[#0E3D95]">
            The <span className="text-[#F88200]">Collection</span>
          </h2>
          <p className="text-lg md:text-xl text-slate-600">Shop our premium American-made hot tubs and spas</p>
        </motion.div>

        {/* Hot Tub Brands - PDC */}
        <div className="flex flex-wrap justify-center gap-6 max-w-5xl mx-auto">
          {brands.map((brand, idx) => (
            <motion.div
              key={brand.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className={`bg-slate-50 rounded-xl p-6 border-t-4 ${brand.border} hover:shadow-lg transition-all text-center w-full sm:w-72`}
            >
              <h3 className="font-['Barlow_Condensed'] text-xl md:text-2xl font-bold uppercase text-[#0E3D95] mb-2">
                {brand.name}
              </h3>
              <p className="text-slate-600 mb-4 text-sm md:text-base">{brand.tagline}</p>
              <Link to={brand.link} className="btn-primary inline-flex items-center gap-2">
                Shop Now <ChevronRight size={18} />
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Sauna Collection */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-20 mb-10"
        >
          <div className="flex justify-center mb-6">
            <div className="flex items-center gap-2">
              <div className="w-16 h-0.5 bg-[#0E3D95]"></div>
              <div className="w-6 h-0.5 bg-[#F88200]"></div>
              <div className="w-16 h-0.5 bg-[#0E3D95]"></div>
            </div>
          </div>
          <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl lg:text-6xl font-black uppercase mb-4 text-[#0E3D95]">
            Sauna <span className="text-[#F88200]">Collection</span>
          </h2>
          <p className="text-lg md:text-xl text-slate-600">Indoor &amp; outdoor infrared saunas for everyday wellness</p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {saunas.map((sauna, idx) => (
            <motion.div
              key={sauna.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
            >
              <Link
                to={sauna.link}
                className="group block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all h-full"
              >
                <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                  <img
                    src={sauna.image}
                    alt={sauna.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => e.target.src = ASSETS.logo}
                  />
                </div>
                <div className="p-6 text-center">
                  <h3 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold uppercase text-[#0E3D95] group-hover:text-[#F88200] transition-colors">
                    {sauna.name}
                  </h3>
                  <p className="text-base text-[#F88200] font-semibold mt-1">{sauna.tagline}</p>
                  <span className="inline-flex items-center gap-2 text-[#0E3D95] text-lg font-bold mt-4 group-hover:text-[#F88200] group-hover:gap-3 transition-all">
                    View Details <ChevronRight size={20} />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-8">
          <Link to="/saunas" className="btn-primary inline-flex items-center gap-2 text-lg">
            View All Saunas <ChevronRight size={20} />
          </Link>
        </div>
      </div>
    </section>
  );
};
// Removed duplicate closing tags

// NEW Why Hot Tubs Section - Rotating Photo Gallery
// Why a Hot Tub - Side by Side with Rotating Gallery and Text
const WhyHotTubSection = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Hot tub gallery images
  const galleryImages = [
    { src: '/images/gallery/Caldera-Paradise-Kauai-Hot-Tub-Sterling-Marble-Coastal-Gray-Lifestyle-Couple-007-scaled.jpg', alt: 'Couple enjoying hot tub together' },
    { src: '/images/gallery/Lady-Relaxing_f84cdca3f3fd7df924a9ef2738140c50.jpg', alt: 'Lady relaxing in hot tub' },
    { src: '/images/gallery/Screenshot 2026-02-28 at 1.36.19 PM.png', alt: 'Hot tub relaxation' },
    { src: '/images/gallery/istockphoto-999140598-612x612.jpg', alt: 'Couple in hot tub' },
    { src: '/images/gallery/pexels-ron-lach-8844607.jpg', alt: 'Hot tub experience' },
  ];

  // Auto-rotate images every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [galleryImages.length]);

  return (
    <section className="py-16 md:py-20" style={{
      background: 'linear-gradient(180deg, #ffffff 0%, #E0FFFF 30%, #AFEEEE 70%, #FFE4B5 100%)'
    }}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Rotating Gallery Side */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative rounded-2xl overflow-hidden shadow-xl aspect-[4/3]"
          >
            <AnimatePresence mode="wait">
              <motion.img
                key={currentImageIndex}
                src={galleryImages[currentImageIndex].src}
                alt={galleryImages[currentImageIndex].alt}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="w-full h-full object-cover absolute inset-0"
              />
            </AnimatePresence>
            {/* Gallery indicators */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {galleryImages.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentImageIndex(idx)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    idx === currentImageIndex ? 'bg-white scale-110' : 'bg-white/50 hover:bg-white/70'
                  }`}
                />
              ))}
            </div>
            {/* Navigation arrows */}
            <button
              onClick={() => setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length)}
              className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/30 hover:bg-black/50 rounded-full text-white transition-colors"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={() => setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/30 hover:bg-black/50 rounded-full text-white transition-colors"
            >
              <ChevronRight size={24} />
            </button>
          </motion.div>

          {/* Text Side */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex justify-start mb-6">
              <div className="flex items-center gap-2">
                <div className="w-12 h-0.5 bg-[#0E3D95]"></div>
                <div className="w-4 h-0.5 bg-[#F88200]"></div>
              </div>
            </div>
            
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl lg:text-6xl font-black uppercase text-[#0E3D95] mb-4">
              Why a{' '}
              <span 
                className="text-[#F88200]"
                style={{ 
                  textShadow: '-2px -2px 0 #fff, 2px -2px 0 #fff, -2px 2px 0 #fff, 2px 2px 0 #fff, -3px 0 0 #fff, 3px 0 0 #fff, 0 -3px 0 #fff, 0 3px 0 #fff'
                }}
              >
                Hot Tub
              </span>?
            </h2>
            
            <p className="text-lg md:text-xl text-slate-600 mb-8">
              For centuries, people have celebrated the soothing properties of warm water to ease aches, pains, and stress. A hot tub brings these benefits right to your backyard.
            </p>
            
            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#0E3D95]/10 rounded-full flex items-center justify-center">
                  <Droplets className="w-6 h-6 text-[#0E3D95]" />
                </div>
                <div>
                  <span className="font-bold text-[#0E3D95]">Hydrotherapy</span>
                  <p className="text-sm text-slate-500">Muscle recovery</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#0E3D95]/10 rounded-full flex items-center justify-center">
                  <Moon className="w-6 h-6 text-[#0E3D95]" />
                </div>
                <div>
                  <span className="font-bold text-[#0E3D95]">Better Sleep</span>
                  <p className="text-sm text-slate-500">Deeper rest</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#0E3D95]/10 rounded-full flex items-center justify-center">
                  <Heart className="w-6 h-6 text-[#0E3D95]" />
                </div>
                <div>
                  <span className="font-bold text-[#0E3D95]">Stress Relief</span>
                  <p className="text-sm text-slate-500">Relaxation</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#0E3D95]/10 rounded-full flex items-center justify-center">
                  <Activity className="w-6 h-6 text-[#0E3D95]" />
                </div>
                <div>
                  <span className="font-bold text-[#0E3D95]">Pain Relief</span>
                  <p className="text-sm text-slate-500">Joint flexibility</p>
                </div>
              </div>
            </div>
            
            <Link to="/wellness" className="btn-primary inline-flex items-center gap-2 text-lg">
              Discover All Benefits <ChevronRight size={22} />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Keep the old sections for reference but we'll use the combined one
const WhyHotTubsSection = () => (
  <section className="py-16 md:py-20" style={{
    background: 'linear-gradient(180deg, #ffffff 0%, #E0FFFF 30%, #AFEEEE 70%, #FFE4B5 100%)'
  }}>
    <div className="max-w-7xl mx-auto px-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-12"
      >
        {/* Subtle accent line */}
        <div className="flex justify-center mb-6">
          <div className="flex items-center gap-2">
            <div className="w-16 h-0.5 bg-[#0E3D95]"></div>
            <div className="w-6 h-0.5 bg-[#F88200]"></div>
            <div className="w-16 h-0.5 bg-[#0E3D95]"></div>
          </div>
        </div>
        <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl lg:text-6xl font-black uppercase text-[#0E3D95] mb-4">
          Why a <span className="text-[#F88200]">Hot Tub</span>?
        </h2>
        <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto">
          For centuries, people have celebrated the soothing properties of warm water to ease aches, pains, and stress.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { icon: Droplets, title: 'Hydrotherapy', desc: 'Improve circulation, muscle recovery, and reduce anxiety.' },
          { icon: Moon, title: 'Better Sleep', desc: 'Warm water and jets melt away stress for better rest.' },
          { icon: Heart, title: 'Stress Relief', desc: 'Release endorphins and unwind in soothing warm water.' },
          { icon: Activity, title: 'Pain Relief', desc: 'Ease joint stiffness and increase flexibility naturally.' },
        ].map((item, idx) => (
          <motion.div
            key={item.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: idx * 0.1 }}
            className="bg-white rounded-lg p-6 text-center hover:shadow-md transition-all border-t-2 border-[#F88200]"
          >
            <item.icon className="w-10 h-10 mx-auto text-[#0E3D95] mb-4" />
            <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-[#0E3D95] mb-2">{item.title}</h3>
            <p className="text-sm text-slate-600">{item.desc}</p>
          </motion.div>
        ))}
      </div>

      <div className="text-center mt-10">
        <Link to="/wellness" className="btn-primary inline-flex items-center gap-2">
          Discover All Benefits <ChevronRight size={20} />
        </Link>
      </div>
    </div>
  </section>
);

// Shop All Models Carousel - Mixed brands from Dynasty, Grand River (no Viking)
const ShopAllModelsSection = () => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // PDC hot tubs, sorted by lowest price first
  const mixedProducts = [...PDC_HOT_TUBS]
    .sort((a, b) => (a.priceValue || 0) - (b.priceValue || 0))
    .slice(0, 12);

  const checkScroll = () => {
    if (scrollRef.current) {
      setCanScrollLeft(scrollRef.current.scrollLeft > 0);
      setCanScrollRight(
        scrollRef.current.scrollLeft < scrollRef.current.scrollWidth - scrollRef.current.clientWidth - 10
      );
    }
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 350;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(checkScroll, 300);
    }
  };

  useEffect(() => {
    checkScroll();
    const ref = scrollRef.current;
    if (ref) {
      ref.addEventListener('scroll', checkScroll);
      return () => ref.removeEventListener('scroll', checkScroll);
    }
  }, []);

  return (
    <section className="py-16" style={{
      background: 'linear-gradient(180deg, #ffffff 0%, #E0FFFF 50%, #E0FFFF 100%)'
    }}>
      <div className="max-w-7xl mx-auto px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex items-center justify-between mb-8"
        >
          <div>
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase text-[#0E3D95]">
              Shop All Models
            </h2>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              className={`p-3 rounded-full transition-all ${canScrollLeft ? 'bg-[#0E3D95] text-white hover:bg-[#F88200]' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
            >
              <ChevronLeft size={28} />
            </button>
            <button 
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              className={`p-3 rounded-full transition-all ${canScrollRight ? 'bg-[#0E3D95] text-white hover:bg-[#F88200]' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
            >
              <ChevronRight size={28} />
            </button>
          </div>
        </motion.div>

        <div 
          ref={scrollRef}
          className="flex gap-6 overflow-x-auto scrollbar-hide pb-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {mixedProducts.map((product, idx) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: Math.min(idx * 0.05, 0.3) }}
              className="flex-shrink-0 w-[320px]"
            >
              <Link 
                to={productLink(product)}
                className="group block bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all"
              >
                <div className="relative aspect-square bg-slate-50 overflow-hidden">
                  {/* Show PRIMARY image (actual hot tub) */}
                  <img 
                    src={product.images?.primary}
                    alt={product.name}
                    className="absolute inset-0 w-full h-full object-contain p-4"
                    onError={(e) => e.target.src = ASSETS.logo}
                  />
                  <div className="absolute top-3 left-3">
                    <span className="bg-[#0E3D95] text-white text-sm px-3 py-1 rounded font-semibold">
                      {product.brand}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-['Barlow_Condensed'] text-xl md:text-2xl font-bold text-[#0E3D95] group-hover:text-[#F88200] transition-colors">
                    {product.name}
                  </h3>
                  <p className="text-base text-slate-500 mt-1">
                    {product.seatsLabel || (product.persons ? `${product.persons} Person` : '')}{product.jets ? ` • ${product.jets} Jets` : ''}
                  </p>
                  <p className="text-sm font-bold text-[#F88200] mt-2">Call or email for pricing</p>
                  <span className="inline-flex items-center gap-1 text-[#0E3D95] text-base font-semibold mt-3 group-hover:text-[#F88200] group-hover:gap-2 transition-all">
                    Learn More <ChevronRight size={18} />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link to="/hot-tubs" className="btn-primary inline-flex items-center gap-2 text-lg">
            View All Products <ChevronRight size={20} />
          </Link>
        </div>
      </div>
    </section>
  );
};

// AR Visualizer Section - Small line blurb
const ARVisualizerSection = () => (
  <section className="py-8" style={lightGradientBg}>
    <div className="max-w-7xl mx-auto px-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        whileInView={{ opacity: 1, y: 0 }} 
        viewport={{ once: true }}
        className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 text-center md:text-left"
      >
        <div className="flex items-center gap-3">
          <Camera className="text-[#D4AF37]" size={28} />
          <span className="font-['Barlow_Condensed'] text-xl md:text-2xl font-bold uppercase text-[#0E3D95]">
            AR Technology
          </span>
        </div>
        <p className="text-lg text-slate-600">
          Want to see how a hot tub looks in your backyard? Try our AR Visualizer on your phone!
        </p>
        <Link 
          to="/ar-visualizer" 
          className="btn-primary inline-flex items-center gap-2 text-base whitespace-nowrap"
        >
          <Smartphone size={18} />
          Try It Now
        </Link>
      </motion.div>
    </div>
  </section>
);

// Product Comparison Section with gradient
const ComparisonSection = () => {
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showSelector, setShowSelector] = useState(false);
  
  // PDC spas plus swim spas, saunas & cold plunges
  const allProducts = [
    ...PDC_HOT_TUBS,
    ...PDC_SWIM_SPAS,
    ...SAUNAS.map(p => ({ ...p, category: 'Sauna' })),
    ...COLD_PLUNGES.map(p => ({ ...p, category: 'Cold Plunge' })),
    ...PDC_COLD_PLUNGES,
  ].sort((a, b) => (a.priceValue || 0) - (b.priceValue || 0));

  const addProduct = (product) => {
    if (selectedProducts.length < 3 && !selectedProducts.find(p => p.id === product.id)) {
      setSelectedProducts([...selectedProducts, product]);
    }
    setShowSelector(false);
  };

  const removeProduct = (productId) => {
    setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
  };

  return (
    <section className="py-16 bg-[#0E3D95]">
      <div className="max-w-7xl mx-auto px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true }} 
          className="text-center mb-10"
        >
          <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase text-white mb-2">
            Compare Products
          </h2>
          <p className="text-lg text-slate-400">Select up to 3 products to compare side-by-side</p>
        </motion.div>

        <div className="bg-white rounded-xl overflow-hidden shadow-xl">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-0">
            <div className="bg-[#0E3D95] text-white p-4 hidden md:block">
              <div className="h-32 flex items-end pb-2">
                <span className="font-['Barlow_Condensed'] text-xl font-bold">Features</span>
              </div>
              <div className="py-3 border-t border-white/20 text-base">Category</div>
              <div className="py-3 border-t border-white/20 text-base">Price</div>
              <div className="py-3 border-t border-white/20 text-base">Capacity</div>
              <div className="py-3 border-t border-white/20 text-base">Jets</div>
              <div className="py-3 border-t border-white/20 text-base">Dimensions</div>
              <div className="py-3 border-t border-white/20 text-base"></div>
            </div>

            {[0, 1, 2].map((index) => {
              const product = selectedProducts[index];
              return (
                <div key={index} className="border-l border-slate-200 first:border-l-0">
                  {product ? (
                    <>
                      <div className="p-4 h-32 flex flex-col items-center justify-center relative bg-slate-50">
                        <button 
                          onClick={() => removeProduct(product.id)}
                          className="absolute top-2 right-2 p-1 bg-red-100 text-red-600 rounded-full hover:bg-red-200"
                        >
                          <X size={16} />
                        </button>
                        <img src={product.images?.primary} alt={product.name} className="h-16 object-contain mb-1" onError={(e) => e.target.src = ASSETS.logo} />
                        <h4 className="font-['Barlow_Condensed'] font-bold text-base text-center">{product.name}</h4>
                      </div>
                      <div className="py-3 px-4 border-t text-center text-base"><span className="inline-block px-3 py-1 bg-[#0E3D95] text-white text-sm rounded font-semibold">{product.category}</span></div>
                      <div className="py-3 px-4 border-t text-center font-bold text-[#F88200] text-sm">Call or email for pricing</div>
                      <div className="py-3 px-4 border-t text-center text-base">{product.seatsLabel || (product.persons ? `${product.persons} Person` : '-')}</div>
                      <div className="py-3 px-4 border-t text-center text-base">{product.jets || '-'}</div>
                      <div className="py-3 px-4 border-t text-center text-sm">{product.dimensions || '-'}</div>
                      <div className="py-3 px-4 border-t text-center">
                        <Link to={productLink(product)} className="text-[#F88200] text-base font-semibold hover:underline">View Details</Link>
                      </div>
                    </>
                  ) : (
                    <div className="h-full min-h-[320px] flex items-center justify-center p-4">
                      <button 
                        onClick={() => setShowSelector(true)}
                        className="flex flex-col items-center gap-3 p-6 border-2 border-dashed border-slate-300 rounded-xl hover:border-[#F88200] hover:bg-red-50 transition-all group"
                      >
                        <div className="w-14 h-14 rounded-full bg-slate-100 group-hover:bg-[#F88200] flex items-center justify-center transition-colors">
                          <Plus className="text-slate-400 group-hover:text-white" size={28} />
                        </div>
                        <span className="font-['Barlow_Condensed'] font-bold text-lg text-slate-600 group-hover:text-[#F88200]">Add Product</span>
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {showSelector && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setShowSelector(false)}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} className="bg-white max-w-2xl w-full max-h-[80vh] rounded-xl overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="bg-[#0E3D95] text-white p-4 flex items-center justify-between">
                <h3 className="font-['Barlow_Condensed'] text-2xl font-bold">Select a Product</h3>
                <button onClick={() => setShowSelector(false)}><X size={28} /></button>
              </div>
              <div className="p-4 overflow-y-auto max-h-[60vh]">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {allProducts.map(product => (
                    <button key={product.id} onClick={() => addProduct(product)} disabled={selectedProducts.find(p => p.id === product.id)} className={`p-3 border rounded-lg text-left hover:border-[#F88200] hover:bg-red-50 transition-all ${selectedProducts.find(p => p.id === product.id) ? 'opacity-50' : ''}`}>
                      <div className="flex items-center gap-3">
                        <img src={product.images?.primary} alt={product.name} className="w-14 h-14 object-contain" onError={(e) => e.target.src = ASSETS.logo} />
                        <div>
                          <h4 className="font-semibold text-base">{product.name}</h4>
                          <p className="text-sm text-slate-500">{product.category}</p>
                          <p className="text-xs font-bold text-[#F88200]">Call or email for pricing</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

// Spa Butler Service Promo Section (links to full Spa Butler page)
const SpaButlerHomeSection = () => (
  <section className="py-14 md:py-20" style={{ background: 'linear-gradient(135deg, #0E3D95 0%, #1a4d8a 60%, #20B2AA 100%)' }} data-testid="spa-butler-home-section">
    <div className="max-w-7xl mx-auto px-4">
      <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
        {/* Text side */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="text-white"
        >
          <span className="inline-block bg-[#F88200] text-white text-sm font-bold uppercase tracking-widest px-4 py-1 rounded-full mb-4">
            Hot Tub Service &amp; Maintenance
          </span>
          <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl lg:text-6xl font-black uppercase leading-none mb-4">
            The <span className="text-[#FFD700]">Spa Butler</span> Service
          </h2>
          <p className="text-lg md:text-xl text-white/90 mb-6">
            Clean, crystal clear water — without the hassle. Factory-trained techs, chemicals included, and a 32-point inspection on every single visit. Choose Weekly, Bi-Weekly or Monthly maintenance plans.
          </p>
          <ul className="space-y-2 mb-8 text-lg">
            <li className="flex items-center gap-3"><span className="text-[#FFD700] font-bold">✓</span> Chemicals included on every plan</li>
            <li className="flex items-center gap-3"><span className="text-[#FFD700] font-bold">✓</span> 32-point inspection each visit</li>
            <li className="flex items-center gap-3"><span className="text-[#FFD700] font-bold">✓</span> No long-term contract</li>
          </ul>
          <div className="flex flex-wrap gap-4">
            <Link to="/spa-butler" className="bg-[#F88200] hover:bg-[#e07600] text-white inline-flex items-center gap-2 text-lg font-bold py-4 px-8 uppercase tracking-wider transition-colors rounded">
              View Membership Plans <ChevronRight size={22} />
            </Link>
            <a href={`tel:${CONTACT.phone.replace(/[^0-9]/g, '')}`} className="border-2 border-white text-white hover:bg-white/10 inline-flex items-center gap-2 text-lg font-bold py-4 px-8 uppercase tracking-wider transition-colors rounded">
              <Phone size={20} /> {CONTACT.phone}
            </a>
          </div>
        </motion.div>

        {/* Graphic side */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="flex justify-center"
        >
          <Link to="/spa-butler" className="block rounded-xl overflow-hidden shadow-2xl ring-4 ring-white/20 hover:ring-[#F88200]/60 transition-all max-w-md">
            <img
              src="/images/spa-butler-plans.webp"
              alt="Spa Butler Maintenance Membership Plans — Weekly $395, Bi-Weekly $295, Monthly $195"
              className="w-full h-auto block"
              loading="lazy"
            />
          </Link>
        </motion.div>
      </div>
    </div>
  </section>
);

// Outdoor Living Section - Tiki bars & showers (links to LeisureCraft page)
const OutdoorLivingSection = () => (
  <section className="py-14 md:py-20 bg-white" data-testid="outdoor-living-section">
    <div className="max-w-7xl mx-auto px-4">
      <div className="text-center mb-10">
        <span className="text-sm font-semibold text-[#F88200] uppercase tracking-wider">Backyard Wellness</span>
        <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl lg:text-6xl font-black uppercase text-[#0E3D95] leading-none">
          Outdoor <span className="text-[#F88200]">Living</span>
        </h2>
        <p className="text-lg text-slate-600 mt-2 max-w-2xl mx-auto">
          Complete your space with Canadian-made cedar tiki bars, outdoor furniture &amp; showers.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-6 max-w-5xl mx-auto">
        {[
          { title: 'Cedar Tiki Bars & Furniture', img: 'https://dundalkleisurecraft.s3.ca-central-1.amazonaws.com/INVENTRY/CT3672N_6PEJ4DIMN0PBSGP0/CT3672N_10.jpg' },
          { title: 'Outdoor Showers', img: 'https://dundalkleisurecraft.s3.ca-central-1.amazonaws.com/INVENTRY/582SN_QV9IMJM5T0PBSGP0/582SN_10.jpg' },
        ].map((c) => (
          <Link key={c.title} to="/leisurecraft" className="group relative rounded-2xl overflow-hidden shadow-lg h-72 md:h-80 block">
            <img src={c.img} alt={c.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <h3 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold uppercase text-white">{c.title}</h3>
              <span className="inline-flex items-center gap-1 text-[#FFD700] font-semibold mt-1 group-hover:gap-2 transition-all">
                Explore <ChevronRight size={18} />
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

// Home Page Component - Rearranged sections
const HomePage = () => {
  const [showPopup, setShowPopup] = useState(false);
  
  useEffect(() => {
    const hasSeenPopup = sessionStorage.getItem('lanaiSpecialPopupSeen');
    if (!hasSeenPopup) {
      const timer = setTimeout(() => { 
        setShowPopup(true); 
        sessionStorage.setItem('lanaiSpecialPopupSeen', 'true'); 
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <>
      <Seo page="/" jsonLd={faqJsonLd} />
      
      <WetTestPopup isOpen={showPopup} onClose={() => setShowPopup(false)} />
      
      {/* 1. Hero Section - Video */}
      <HeroSection />
      
      {/* 2. Trust Badges */}
      <TrustBadgesSection />
      
      {/* 3. Expand Your Wellness Journey (Hot Tubs, Saunas, Cold Plunges, Swim Spas) */}
      <ProductCollectionSection />

      <WhyBuySection />

      {/* Brand video */}
      <section className="max-w-5xl mx-auto px-4">
        <VideoBlock
          eyebrow="Welcome to Leisure Wellness Naples"
          title="Your Southwest Florida Wellness Destination"
          subtitle="Hot tubs, saunas, cold plunges & swim spas — see what makes our Naples showroom different."
          src={VIDEOS.brand}
          reel={REELS.brand}
          testId="home-brand-video"
        />
        <VideoBlock
          eyebrow="A Daily Ritual"
          reel={REELS.journey}
          testId="home-journey-video"
        />
      </section>
      
      {/* 4. Why a Hot Tub - Side by Side with Image */}
      <WhyHotTubSection />
      
      {/* 5. Why Leisure Wellness Naples - Wet Test Section with Video */}
      <WetTestSection />
      
      {/* 6. Shop All Models Carousel */}
      <ShopAllModelsSection />
      
      {/* 7. Best Warranty Section with links */}
      <BestWarrantySection />
      
      {/* 8. Free Items Section */}
      <FreeItemsSection />
      
      {/* 9. The Collection (PDC Spas & Saunas) */}
      <LocationCollectionSection />
      
      {/* 10. Product Comparison */}
      <ComparisonSection />
      
      {/* 11. Resource Cards */}
      <ResourceCardsSection />
      
      {/* 12. Spa Butler Service */}
      <SpaButlerHomeSection />
      
      {/* 13. Outdoor Living (Tiki Bars & Showers) */}
      <OutdoorLivingSection />

      {/* 13. AR Visualizer Section */}
      <ARVisualizerSection />

      <FaqSection />
    </>
  );
};

export default HomePage;
